import { useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { isDateWithinRange, useControlledValue } from '../../utils';
export function convertToDatePickerValue(date) {
    if (date instanceof Date) {
        return { start: date, end: date };
    }
    return date;
}
export function getNormalizedValue(value, direction) {
    if (!value) {
        return;
    }
    if (direction === 'start' || direction === 'end') {
        return value[direction];
    }
    return value.start;
}
export function getNextDatePickerValue(date, selectedDates, options = {}) {
    const { allowRange, change } = options;
    if (change === 'both' && !allowRange) {
        return convertToDatePickerValue(date);
    }
    if ((selectedDates === null || selectedDates === void 0 ? void 0 : selectedDates.start) != null &&
        ((change === 'both' && selectedDates.end == null) || change === 'end') &&
        date >= selectedDates.start) {
        return { start: selectedDates.start, end: date };
    }
    if (change === 'start' &&
        (selectedDates === null || selectedDates === void 0 ? void 0 : selectedDates.end) != null &&
        date <= selectedDates.end) {
        return { start: date, end: selectedDates.end };
    }
    if ((selectedDates === null || selectedDates === void 0 ? void 0 : selectedDates.start) != null &&
        (change === 'both' || change === 'end') &&
        selectedDates.end == null &&
        date <= selectedDates.start) {
        return { start: date, end: selectedDates.start };
    }
    return { start: date };
}
export function initCurrentDate(value, direction = 'both') {
    if (value instanceof Date) {
        return value;
    }
    if (['end', 'both'].includes(direction) && (value === null || value === void 0 ? void 0 : value.end) instanceof Date) {
        return value.end;
    }
    if ((value === null || value === void 0 ? void 0 : value.start) instanceof Date) {
        return value.start;
    }
    return new Date();
}
export function useDatePickerState({ value, onChange, defaultValue, direction = 'both', visibleMonths = 1, minValue, maxValue, allowRange, }) {
    const [selectedDates, setSelectedDates] = useControlledValue({
        value: convertToDatePickerValue(value),
        onChange,
        defaultValue: convertToDatePickerValue(defaultValue),
    });
    const [currentDate, setCurrentDate] = useState(() => initCurrentDate(selectedDates, direction));
    const [hoveredDates, setHoveredDates] = useState();
    const [prevValue, setPrevValue] = useState(value);
    const [period, setPeriod] = useState('day');
    // when the value changes, make sure we display a calendar month that is in range
    if (prevValue !== value) {
        setPrevValue(value);
        const normalizedValue = convertToDatePickerValue(value);
        if (normalizedValue &&
            !isDateWithinRange(currentDate, normalizedValue.start, normalizedValue.end)) {
            setCurrentDate(initCurrentDate(value, direction));
        }
    }
    // using memo here cause we would create infinite rerenders by passing down a new Date object each time
    const months = useMemo(() => {
        if (period === 'month') {
            return Array.from({ length: 12 }).map((_, index) => new Date(currentDate.getFullYear(), index, currentDate.getDate()));
        }
        if (period === 'year') {
            return Array.from({ length: 20 })
                .map((_, index) => new Date(currentDate.getFullYear() - index, currentDate.getMonth(), currentDate.getDate()))
                .sort((a, b) => new Date(a).valueOf() - new Date(b).valueOf());
        }
        return Array.from(Array(visibleMonths)).map((_, index) => new Date(currentDate.getFullYear(), currentDate.getMonth() + index, 1));
    }, [currentDate, visibleMonths, period]);
    const getPreviousPeriod = (date, monthOfYear = date.getMonth(), yearPeriod = 20) => {
        if (period === 'day') {
            return new Date(date.getFullYear(), date.getMonth() - visibleMonths, 1);
        }
        if (period === 'month') {
            return new Date(date.getFullYear() - 1, date.getMonth(), 1);
        }
        return new Date(date.getFullYear() - yearPeriod, monthOfYear, 1);
    };
    const getNextPeriod = (date, monthOfYear = date.getMonth(), yearPeriod = 20) => {
        if (period === 'day') {
            return new Date(date.getFullYear(), date.getMonth() + visibleMonths, 1);
        }
        if (period === 'month') {
            return new Date(date.getFullYear() + 1, date.getMonth(), 1);
        }
        return new Date(date.getFullYear() + yearPeriod, monthOfYear, 1);
    };
    const isPreviousPeriodDisabled = !isDateWithinRange(getPreviousPeriod(months[0], 11), minValue
        ? new Date(minValue.getFullYear(), minValue.getMonth(), 1)
        : undefined, maxValue
        ? new Date(maxValue.getFullYear(), maxValue.getMonth(), 1)
        : undefined);
    const isNextPeriodDisabled = !isDateWithinRange(getNextPeriod(months[months.length - 1], 0, 1), minValue
        ? new Date(minValue.getFullYear(), minValue.getMonth(), 1)
        : undefined, maxValue
        ? new Date(maxValue.getFullYear(), maxValue.getMonth(), 1)
        : undefined);
    const setPreviousPeriod = () => !isPreviousPeriodDisabled &&
        flushSync(() => {
            setCurrentDate((date) => getPreviousPeriod(date));
        });
    const setNextPeriod = () => !isNextPeriodDisabled &&
        flushSync(() => {
            setCurrentDate((date) => getNextPeriod(date));
        });
    const setStartOrEndDate = (date) => {
        setSelectedDates((selectedDates) => getNextDatePickerValue(date, selectedDates, {
            allowRange,
            change: direction,
        }));
        setHoveredDates(undefined);
    };
    const setHoveredStartOrEndDate = (date) => {
        if (!allowRange || !selectedDates) {
            return;
        }
        if (selectedDates.start != null &&
            ((direction === 'both' && selectedDates.end == null) ||
                direction === 'end') &&
            date >= selectedDates.start) {
            setHoveredDates({
                start: selectedDates.start,
                end: date,
            });
        }
        else if (direction === 'start' &&
            selectedDates.end != null &&
            date <= selectedDates.end) {
            setHoveredDates({
                start: date,
                end: selectedDates.end,
            });
        }
        else if (selectedDates.start != null &&
            (direction === 'both' || direction === 'end') &&
            selectedDates.end == null &&
            date <= selectedDates.start) {
            setHoveredDates({
                start: date,
                end: selectedDates.start,
            });
        }
        else {
            setHoveredDates(undefined);
        }
    };
    const removeHoveredDates = () => {
        setHoveredDates(undefined);
    };
    return {
        currentDate,
        months,
        selectedDates,
        hoveredDates,
        setPreviousPeriod,
        setNextPeriod,
        setStartOrEndDate,
        setHoveredStartOrEndDate,
        removeHoveredDates,
        isPreviousPeriodDisabled,
        isNextPeriodDisabled,
        period,
        setPeriod,
        setCurrentDate,
    };
}
//# sourceMappingURL=DatePickerUtils.js.map